/**
 * AdminTour — lightweight first-time onboarding overlay for the admin
 * dashboard. Walks new operators through the 5 most important tabs.
 *
 *   - Dismissal persisted in localStorage["ekselens.adminTour.dismissed"]
 *   - "Skip" or "Got it" both dismiss permanently
 *   - Step rendered as a portal-style modal over a dim backdrop (no
 *     react-portal needed — z-index on a fixed element is enough)
 *   - Accessible: ESC closes, focus trapped on the action buttons
 */
import React, { useEffect, useState, useCallback } from "react";
import { ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";

const STORAGE_KEY = "ekselens.adminTour.dismissed";

const STEPS = [
  {
    title: "Welcome to the EKSELENS360 admin",
    body: "This is a one-time 5-step tour of the most important tabs. You can dismiss it whenever — we won't surface it again. (To re-open, clear localStorage key `ekselens.adminTour.dismissed`.)",
    tabName: null,
  },
  {
    title: "Assessments — the operational core",
    body: "Open a run to access Plan / Evidence / CSI panels and the four branded exports. Plan, Evidence-review and CSI-tailoring are background Opus jobs (~90s) that cache automatically.",
    tabName: "Assessments",
  },
  {
    title: "Rate cards — deterministic pricing",
    body: "The Boutique — UK Senior Delivery card seeds at ~70–80% of CCS RM6263 Tier-1 bands. Clone it before editing if you want to preserve the seed. Modifiers (region/sector/size) feed into every plan price audit.",
    tabName: "Rate cards",
  },
  {
    title: "Ops — scheduler health & lite history",
    body: "Three colour-coded job pills (lead backup / weekly stakeholder PDF / weekly SEO digest / daily draft nudge). Anonymous lite history shows every device that's run a self-assessment — once they share an email, the rows light up identified.",
    tabName: "Ops",
  },
  {
    title: "Help — defensible methodology",
    body: "A full operator guide plus a downloadable Methodology Paper PDF you can hand to procurement when they ask 'what's actually under the bonnet'. We recommend reading it once before your first engagement.",
    tabName: "Help",
  },
];

const AdminTour = () => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      const dismissed = localStorage.getItem(STORAGE_KEY) === "true";
      if (!dismissed) setOpen(true);
    } catch { /* localStorage unavailable */ }
  }, []);

  const dismiss = useCallback(() => {
    try { localStorage.setItem(STORAGE_KEY, "true"); } catch { /* noop */ }
    setOpen(false);
  }, []);

  const switchTab = useCallback((name) => {
    if (!name) return;
    try { window.dispatchEvent(new CustomEvent("admin-switch-tab", { detail: name })); } catch { /* noop */ }
  }, []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") dismiss();
      if (e.key === "ArrowRight" && step < STEPS.length - 1) setStep((s) => s + 1);
      if (e.key === "ArrowLeft" && step > 0) setStep((s) => s - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, step, dismiss]);

  if (!open) return null;
  const current = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[60] bg-black/65 backdrop-blur-[2px] flex items-center justify-center px-4" data-testid="admin-tour-overlay">
      <div className="bg-white max-w-lg w-full p-7 border border-zinc-300 shadow-2xl relative">
        <button
          type="button"
          onClick={dismiss}
          className="absolute top-3 right-3 text-zinc-400 hover:text-zinc-700"
          aria-label="Close tour"
          data-testid="admin-tour-close"
        >
          <X size={18} />
        </button>
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-[#7737F6]" />
          <div className="ek-eyebrow text-[#7737F6] text-[11px]">First-time tour · {step + 1} / {STEPS.length}</div>
        </div>
        <h3 className="ek-heading text-xl text-black mt-3" data-testid="admin-tour-title">{current.title}</h3>
        <p className="text-[14.5px] text-zinc-700 mt-3 font-light leading-relaxed">{current.body}</p>
        {current.tabName ? (
          <button
            type="button"
            onClick={() => switchTab(current.tabName)}
            className="mt-4 text-[12.5px] text-[#7737F6] hover:text-[#5D28C7] inline-flex items-center gap-1"
            data-testid={`admin-tour-open-tab-${current.tabName.toLowerCase().replace(/\s+/g, "-")}`}
          >
            Open the {current.tabName} tab →
          </button>
        ) : null}

        <div className="mt-7 flex items-center justify-between">
          <button
            type="button"
            onClick={dismiss}
            className="text-[12.5px] text-zinc-500 hover:text-zinc-800"
            data-testid="admin-tour-skip"
          >
            Skip tour
          </button>
          <div className="flex gap-2">
            {!isFirst ? (
              <button
                type="button"
                onClick={() => setStep((s) => s - 1)}
                className="inline-flex items-center gap-1 px-4 py-2 border border-zinc-300 text-zinc-800 hover:bg-zinc-50 text-[13px]"
                data-testid="admin-tour-prev"
              >
                <ChevronLeft size={14} /> Back
              </button>
            ) : null}
            {!isLast ? (
              <button
                type="button"
                onClick={() => setStep((s) => s + 1)}
                className="ek-btn ek-btn-primary inline-flex items-center gap-1 px-4 py-2 text-[13px]"
                data-testid="admin-tour-next"
              >
                Next <ChevronRight size={14} />
              </button>
            ) : (
              <button
                type="button"
                onClick={dismiss}
                className="ek-btn ek-btn-primary inline-flex items-center gap-1 px-4 py-2 text-[13px]"
                data-testid="admin-tour-finish"
              >
                Got it
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminTour;
